import React, { useMemo, useState } from "react";
import { Flex, Text, Box, SimpleGrid } from "@chakra-ui/react";
import { type GetStaticProps, type NextPage } from "next";
import {
  getResources,
  type ResourceSimple,
  type Page as SanityPage,
  getPage,
  getResourceCategories,
  getResourceTypes,
  type ResourceCategory,
  type ResourceType,
  type Banner,
  getAnnouncementBanner,
} from "lib/sanity";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { SimpleTextHero } from "components/hero";
import { SidebarButton, SidebarDemo } from "components/sidebar";
import { ResourceLibraryCard } from "components/resources/resource-library-card";
import { useHeaderHeight } from "contexts/header-height";
import { getPageMetaImage } from "utils";
import { Button } from "ui/button";

type PageProps = {
  page: SanityPage;
  categories: ResourceCategory[];
  types: ResourceType[];
  posts: ResourceSimple[];
  announcementBanner?: Banner;
};

const Resources: NextPage<Readonly<PageProps>> = ({ page, categories, types, posts: allPosts, announcementBanner }) => {
  const [selectedCategory, setSelectedCategory] = useState<ResourceCategory | undefined>();
  const [selectedType, setSelectedType] = useState<ResourceType | undefined>();
  const { headerHeight } = useHeaderHeight();

  const posts = useMemo(() => {
    if (selectedCategory && selectedType) {
      return allPosts.filter(
        (post) => post.category.name === selectedCategory.name && post.type.name === selectedType.name,
      );
    }

    if (selectedCategory) {
      return allPosts.filter((post) => post.category.name === selectedCategory.name);
    }

    if (selectedType) {
      return allPosts.filter((post) => post.type.name === selectedType.name);
    }

    return allPosts;
  }, [allPosts, selectedCategory, selectedType]);

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <SimpleTextHero title={page.title} description={page.description} />

      <Section center my="0" pb={sectionSpacingY}>
        <Flex align="start" w="100%" gap="biggest">
          <Box
            as="aside"
            minW="260px"
            display={{ base: "none", lg: "block" }}
            pos="sticky"
            top={`${headerHeight + 20}px`}
            height="max-content"
            // MaxHeight = headerHeight + 20px top padding + 20px bottom padding for breathing room
            maxHeight={`calc(100vh - ${headerHeight + 40}px)`}
            flex={0}
            overflowY="auto"
          >
            <Flex direction="column" mb="small">
              <Text textStyle="overline1" fontWeight="semibold" ml="tiny" mb="tinier">
                Categories
              </Text>
              <SidebarButton
                isActive={selectedCategory === undefined}
                onClick={() => {
                  setSelectedCategory(undefined);
                }}
              >
                All
              </SidebarButton>
              {categories.map((category) => (
                <SidebarButton
                  key={category.slug}
                  isActive={category === selectedCategory}
                  onClick={() => {
                    setSelectedCategory(category);
                  }}
                >
                  {category.name}
                </SidebarButton>
              ))}
            </Flex>

            <Flex direction="column" mb="smaller">
              <Text textStyle="overline1" fontWeight="semibold" ml="tiny" mb="tinier">
                Resource types
              </Text>
              <SidebarButton
                isActive={selectedType === undefined}
                onClick={() => {
                  setSelectedType(undefined);
                }}
              >
                All
              </SidebarButton>
              {types.map((type) => (
                <SidebarButton
                  key={type.slug}
                  isActive={type === selectedType}
                  onClick={() => {
                    setSelectedType(type);
                  }}
                >
                  {type.name}
                </SidebarButton>
              ))}
            </Flex>

            <SidebarDemo />
          </Box>

          {posts?.length && posts.length > 0 ? (
            <SimpleGrid as="ul" listStyleType="none" columns={{ base: 1, md: 2, xl: 3 }} gap={6}>
              {posts?.map((post) => {
                return <ResourceLibraryCard key={post.resourceFileName} {...post} />;
              })}
            </SimpleGrid>
          ) : (
            <Flex align="start" direction="column" minW={0} pl="big">
              <Text textStyle="headline1">No results found</Text>
              <Text color="brand.textSecondary.light" textStyle="body3" mt="smaller">
                Reset your filters to continue exploring.
              </Text>
              <Button
                hasRightChevron
                mt="small"
                onClick={() => {
                  setSelectedType(undefined);
                  setSelectedCategory(undefined);
                }}
              >
                Reset filters
              </Button>
            </Flex>
          )}
        </Flex>
      </Section>
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [categories, types, posts, page, announcementBanner] = await Promise.all([
    getResourceCategories(),
    getResourceTypes(),
    getResources(),
    getPage("/resources"),
    getAnnouncementBanner(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      categories,
      types,
      page,
      posts,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export default Resources;
